import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { Code2, Shield, Network, Terminal, CheckCircle2 } from 'lucide-react';

export const Slide10Team: React.FC = () => {
  return (
    <DeckSlideWrapper
      slideNumber={10}
      totalSlides={12}
      category="The Team"
      title="Engineering DNA: Systems, Security &amp; Purity"
      subtitle="A deeply technical team with strong foundations in functional programming, distributed systems, and enterprise security."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-5 items-stretch">
        {/* Left Core Competencies */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3.5">
          <div className="p-3.5 sm:p-4 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gold-500/15 text-gold-800 dark:text-gold-400 flex items-center justify-center mb-2">
              <Code2 className="w-4 h-4" />
            </div>
            <h4 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white mb-1">
              Functional Programming Mastery
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Deep roots in Haskell and strict TypeScript FP. Systems built with mathematical purity, exhaustive sum types, and zero runtime exception risks.
            </p>
          </div>

          <div className="p-3.5 sm:p-4 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-2">
              <Shield className="w-4 h-4" />
            </div>
            <h4 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white mb-1">
              Cybersecurity &amp; IAM Depth
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Expertise in enterprise identity protocols (SAML 2.0, LDAP, OIDC), cryptographic auditing, and zero-trust data plane isolation.
            </p>
          </div>

          <div className="p-3.5 sm:p-4 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-purple-500/15 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-2">
              <Network className="w-4 h-4" />
            </div>
            <h4 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white mb-1">
              Distributed Edge &amp; Cloud Systems
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Extensive experience in Cloudflare Workers edge architecture, asynchronous queues, low-latency proxies, and containerized on-prem topologies.
            </p>
          </div>

          <div className="p-3.5 sm:p-4 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-2">
              <Terminal className="w-4 h-4" />
            </div>
            <h4 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white mb-1">
              Full-Stack Velocity
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Rapid execution from low-level tokenizers and async Haskell parsers to polished enterprise Next.js dashboards and client SDKs.
            </p>
          </div>
        </div>

        {/* Right Engineering Principles & Hiring Profile */}
        <div className="lg:col-span-5 flex flex-col justify-between p-3.5 sm:p-4.5 rounded-2xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 shadow-surface space-y-2.5 sm:space-y-3">
          <div className="border-b border-slate-800 dark:border-navy-800 pb-2">
            <h4 className="font-mono text-xs font-bold text-white uppercase tracking-wider">
              Core Team Values &amp; Culture
            </h4>
          </div>

          <div className="space-y-2 sm:space-y-2.5 text-[11px] sm:text-xs text-slate-300">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <span><strong>Product-Grade Always:</strong> No disposable code. Every subsystem is built for enterprise longevity.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <span><strong>Open-Core Transparency:</strong> Open source credibility combined with enterprise-grade security extensions.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <span><strong>Capital Efficiency:</strong> Lean, engineering-first culture that maximizes software gross margins and ROI.</span>
            </div>
          </div>

          <div className="p-2.5 sm:p-3 rounded-xl bg-slate-950 dark:bg-navy-900 border border-slate-800 dark:border-navy-800 text-[10px] sm:text-[11px] font-mono text-slate-400">
            <span className="text-gold-400 font-bold">Planned Seed Hires:</span>
            <div className="text-white mt-1 leading-relaxed">
              • 2x Distributed Systems / Rust &amp; Haskell Engineers<br />
              • 1x Developer Relations / Solutions Architect
            </div>
          </div>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
